import { cva } from 'class-variance-authority';
import { interactiveBase, focusRing, transitionAll } from '@/lib/styleUtils';

export const buttonVariants = cva(
  // Base classes applied to all buttons
  [interactiveBase, focusRing, transitionAll, 'tw:w-fit tw:h-fit'].join(' '),
  {
    variants: {
      buttonType: {
        button: '',
        text: 'tw:bg-transparent tw:border-none',
        ghost: 'tw:bg-transparent tw:border-none',
      },
      size: {
        small: 'tw:text-12 tw:font-regular tw:leading-1',
        medium: 'tw:text-14 tw:font-semibold tw:leading-1',
        large: 'tw:text-16 tw:font-semibold tw:leading-3',
      },
      variant: {
        primary: '',
        secondary: '',
        danger: '',
        warn: '',
      },
      isLoading: {
        true: 'tw:cursor-wait tw:pointer-events-none',
        false: '',
      },
    },

    compoundVariants: [
      // ===========================================
      // BUTTON TYPE LAYOUTS (buttonType + size)
      // ===========================================
      {
        buttonType: 'button',
        size: 'small',
        className:
          'tw:min-h-[1.25rem] tw:min-w-[2.5rem] tw:py-nano tw:px-micro tw:gap-[0.125rem] tw:rounded-4',
      },
      {
        buttonType: 'button',
        size: 'medium',
        className:
          'tw:min-h-[2.5rem] tw:min-w-[3.625rem] tw:py-micro tw:px-base tw:gap-micro tw:rounded-8',
      },
      {
        buttonType: 'button',
        size: 'large',
        className:
          'tw:min-h-[3rem] tw:min-w-[5rem] tw:py-micro tw:px-base tw:gap-micro tw:rounded-8',
      },

      // GHOST TYPE LAYOUTS (buttonType + size) — same padded shape as `button`.
      {
        buttonType: 'ghost',
        size: 'small',
        className:
          'tw:min-h-[1.25rem] tw:min-w-[2.5rem] tw:py-nano tw:px-micro tw:gap-[0.125rem] tw:rounded-4',
      },
      {
        buttonType: 'ghost',
        size: 'medium',
        className:
          'tw:min-h-[2.5rem] tw:min-w-[3.625rem] tw:py-micro tw:px-base tw:gap-micro tw:rounded-8',
      },
      {
        buttonType: 'ghost',
        size: 'large',
        className:
          'tw:min-h-[3rem] tw:min-w-[5rem] tw:py-micro tw:px-base tw:gap-micro tw:rounded-8',
      },

      // TEXT TYPE LAYOUTS (buttonType + size)
      {
        buttonType: 'text',
        size: 'small',
        className: 'tw:min-h-[1rem] tw:gap-[0.125rem] tw:rounded-2',
      },
      {
        buttonType: 'text',
        size: 'medium',
        className: 'tw:min-h-[1rem] tw:gap-[0.125rem] tw:rounded-2',
      },
      {
        buttonType: 'text',
        size: 'large',
        className: 'tw:min-h-[1.5rem] tw:px-nano tw:gap-nano tw:rounded-2',
      },

      // ===========================================
      // BUTTON VARIANTS (buttonType=button + variant)
      // ===========================================
      {
        buttonType: 'button',
        variant: 'primary',
        className: [
          'tw:bg-primary tw:text-[#FFFFFF] tw:border tw:border-solid tw:border-primary',
          'tw:shadow-[0_0.125rem_0.3125rem_0_rgba(76,89,107,0.1)]',
          'tw:hover:bg-primary-dark tw:hover:border-primary-dark',
          'tw:active:bg-primary-darker tw:active:border-primary-darker tw:active:scale-[0.98]',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:shadow-none tw:disabled:bg-gray tw:disabled:text-white tw:disabled:border-gray',
        ].join(' '),
      },
      {
        buttonType: 'button',
        variant: 'secondary',
        className: [
          'tw:bg-white tw:text-gray-darker tw:border tw:border-solid tw:border-gray',
          'tw:shadow-[0_0.125rem_0.3125rem_0_rgba(76,89,107,0.1)]',
          'tw:hover:text-primary-dark tw:hover:bg-gray-lighter',
          'tw:active:text-primary-darker tw:active:bg-gray-lighter tw:active:scale-[0.98]',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:shadow-none tw:disabled:bg-white tw:disabled:text-gray tw:disabled:border-gray',
        ].join(' '),
      },
      {
        buttonType: 'button',
        variant: 'danger',
        className: [
          'tw:bg-error tw:text-white tw:border tw:border-solid tw:border-error',
          'tw:shadow-[0_0.125rem_0.3125rem_0_rgba(76,89,107,0.1)]',
          'tw:active:scale-[0.98]',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:shadow-none tw:disabled:bg-gray tw:disabled:text-white tw:disabled:border-gray',
        ].join(' '),
      },
      {
        buttonType: 'button',
        variant: 'warn',
        className: [
          'tw:bg-warn tw:text-white tw:border tw:border-solid tw:border-warn',
          'tw:shadow-[0_0.125rem_0.3125rem_0_rgba(76,89,107,0.1)]',
          'tw:hover:opacity-90',
          'tw:active:scale-[0.98]',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:shadow-none tw:disabled:bg-gray tw:disabled:text-white tw:disabled:border-gray',
        ].join(' '),
      },

      // ===========================================
      // GHOST VARIANTS (buttonType=ghost + variant) — transparent, colored label,
      // subtle gray-lighter hover/press fill.
      // ===========================================
      {
        buttonType: 'ghost',
        variant: 'primary',
        className: [
          'tw:text-primary',
          'tw:hover:bg-gray-lighter tw:hover:text-primary-dark',
          'tw:active:bg-gray-lighter tw:active:text-primary-darker tw:active:scale-[0.98]',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:bg-transparent tw:disabled:text-gray',
        ].join(' '),
      },
      {
        buttonType: 'ghost',
        variant: 'secondary',
        className: [
          'tw:text-gray-darker',
          'tw:hover:bg-gray-lighter tw:hover:text-primary-dark',
          'tw:active:bg-gray-lighter tw:active:text-primary-darker tw:active:scale-[0.98]',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:bg-transparent tw:disabled:text-gray',
        ].join(' '),
      },
      {
        buttonType: 'ghost',
        variant: 'danger',
        className: [
          'tw:text-error',
          'tw:hover:bg-gray-lighter',
          'tw:active:bg-gray-lighter tw:active:scale-[0.98]',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:bg-transparent tw:disabled:text-gray',
        ].join(' '),
      },
      {
        buttonType: 'ghost',
        variant: 'warn',
        className: [
          'tw:text-warn',
          'tw:hover:bg-gray-lighter',
          'tw:active:bg-gray-lighter tw:active:scale-[0.98]',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:bg-transparent tw:disabled:text-gray',
        ].join(' '),
      },

      // ===========================================
      // TEXT VARIANTS (buttonType=text + variant)
      // ===========================================
      {
        buttonType: 'text',
        variant: 'primary',
        className: [
          'tw:text-primary',
          'tw:hover:text-primary-dark',
          'tw:active:text-primary-darker',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:text-gray',
        ].join(' '),
      },
      {
        buttonType: 'text',
        variant: 'secondary',
        className: [
          'tw:text-gray-darker',
          'tw:hover:text-primary-dark',
          'tw:active:text-primary-darker',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:text-gray',
        ].join(' '),
      },
      {
        buttonType: 'text',
        variant: 'danger',
        className: [
          'tw:text-error',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:text-gray',
        ].join(' '),
      },
      {
        buttonType: 'text',
        variant: 'warn',
        className: [
          'tw:text-warn',
          'tw:disabled:cursor-not-allowed tw:disabled:pointer-events-none tw:disabled:text-gray',
        ].join(' '),
      },
    ],

    defaultVariants: {
      buttonType: 'button',
      size: 'medium',
      variant: 'primary',
      isLoading: false,
    },
  }
);
